/* ============================================================
   DSR/ASR shared upload previews — create as assets/dsr-upload-preview.js
   Auto-hooks every .dsr-file-wrap dropzone on the page (both the
   DSR and ASR forms use identical dropzone markup). Renders a
   thumbnail chip per selected file (images get a real preview,
   PDFs/other files get an extension badge) with a remove (×)
   button that rebuilds the input's FileList via DataTransfer.
   No changes to either form's submit plumbing — the inputs'
   .files remain the single source of truth.
   ============================================================ */
(function () {
  'use strict';

  var MAX_FILE_MB    = 25;
  var MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

  function setSizeError(wrap, rejectedNames) {
    var existing = wrap.querySelector('[data-dsr-size-error]');
    if (!rejectedNames.length) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('div');
      existing.className = 'dsr-error-panel';
      existing.setAttribute('data-dsr-size-error', '');
      wrap.appendChild(existing);
    }
    var names = rejectedNames.map(function (n) { return '“' + n + '”'; }).join(', ');
    existing.textContent = names + (rejectedNames.length > 1 ? ' are' : ' is') +
      ' over ' + MAX_FILE_MB + 'MB and was removed. Please choose a smaller file or trim the video.';
  }

  // Strips files over the limit out of the input's FileList.
  function enforceMaxSize(wrap, input) {
    var files    = Array.from(input.files || []);
    var rejected = files.filter(function (f) { return f.size > MAX_FILE_BYTES; });
    if (rejected.length) {
      var kept = files.filter(function (f) { return f.size <= MAX_FILE_BYTES; });
      try {
        var dt = new DataTransfer();
        kept.forEach(function (f) { dt.items.add(f); });
        input.files = dt.files;
      } catch (_) {
        input.value = '';
      }
    }
    setSizeError(wrap, rejected.map(function (f) { return f.name; }));
  }

  // Mirrors the forms' own filename display after we filter the FileList.
  function syncNameDisplay(wrap, input) {
    var nameEl = wrap.querySelector('[data-dsr-file-name]');
    var zone   = wrap.querySelector('.dsr-dropzone');
    var files  = Array.from(input.files || []);
    if (nameEl) nameEl.textContent = files.length ? files.map(function (f) { return f.name; }).join(', ') : '';
    if (zone) {
      if (files.length) zone.dataset.hasFile = 'true';
      else delete zone.dataset.hasFile;
    }
  }

  function fmtSize(bytes) {
    if (typeof bytes !== 'number' || isNaN(bytes)) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  function ensureHost(wrap) {
    var host = wrap.querySelector('[data-dsr-previews]');
    if (!host) {
      host = document.createElement('div');
      host.className = 'dsr-upload-previews';
      host.setAttribute('data-dsr-previews', '');
      wrap.appendChild(host);
    }
    return host;
  }

  function removeAt(wrap, input, index) {
    var files = Array.from(input.files || []);
    files.splice(index, 1);
    try {
      var dt = new DataTransfer();
      files.forEach(function (f) { dt.items.add(f); });
      input.files = dt.files;
    } catch (_) {
      // Very old browsers can't rebuild a FileList — clear everything instead.
      input.value = '';
    }
    render(wrap);
    // Let the form's own change handler update its filename display,
    // validation and step visibility.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function render(wrap) {
    var input = wrap.querySelector('input[type="file"]');
    if (!input) return;
    var host = ensureHost(wrap);

    // Revoke object URLs from the previous render to avoid leaks.
    host.querySelectorAll('img[data-objurl]').forEach(function (img) {
      try { URL.revokeObjectURL(img.src); } catch (_) {}
    });
    host.innerHTML = '';

    var files = Array.from(input.files || []);
    if (files.length) wrap.setAttribute('data-has-previews', '');
    else wrap.removeAttribute('data-has-previews');

    files.forEach(function (file, i) {
      var chip = document.createElement('div');
      chip.className = 'dsr-upload-preview';

      var thumb = document.createElement('div');
      thumb.className = 'dsr-upload-preview__thumb';
      if (file.type && file.type.indexOf('image/') === 0) {
        var img = document.createElement('img');
        img.alt = file.name;
        img.setAttribute('data-objurl', '');
        img.src = URL.createObjectURL(file);
        thumb.appendChild(img);
      } else {
        var ext = document.createElement('span');
        ext.className = 'dsr-upload-preview__ext';
        ext.textContent = ((file.name.split('.').pop() || 'FILE').toUpperCase()).slice(0, 4);
        thumb.appendChild(ext);
      }

      var meta = document.createElement('div');
      meta.className = 'dsr-upload-preview__meta';
      var name = document.createElement('div');
      name.className = 'dsr-upload-preview__name';
      name.textContent = file.name;
      var size = document.createElement('div');
      size.className = 'dsr-upload-preview__size';
      size.textContent = fmtSize(file.size);
      meta.appendChild(name);
      meta.appendChild(size);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dsr-upload-preview__remove';
      remove.setAttribute('aria-label', 'Remove ' + file.name);
      remove.textContent = '×';
      remove.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        removeAt(wrap, input, i);
      });

      chip.appendChild(thumb);
      chip.appendChild(meta);
      chip.appendChild(remove);
      host.appendChild(chip);
    });
  }

  function init() {
    document.querySelectorAll('.dsr-file-wrap').forEach(function (wrap) {
      render(wrap);
      var zone = wrap.querySelector('.dsr-dropzone');
      if (zone) {
        zone.addEventListener('drop', function () {
          setTimeout(function () {
            var input = wrap.querySelector('input[type="file"]');
            if (input) {
              enforceMaxSize(wrap, input);
              syncNameDisplay(wrap, input);
            }
            render(wrap);
          }, 0);
        });
      }
    });
    document.addEventListener('change', function (ev) {
      if (ev.target && ev.target.type === 'file') {
        var wrap = ev.target.closest('.dsr-file-wrap');
        if (wrap) {
          enforceMaxSize(wrap, ev.target);
          render(wrap);
        }
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();